import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { clsx } from '../../ds/clsx';
import { Banner, Caret, Eyebrow, Popover, Skeleton } from '../../ds/components';
import { errorMessage } from '../../lib/api';
import { isForbidden, useControllingStrip, type StripResponse, type StripScope } from './api';
import styles from './ControllingStrip.module.css';

/** Scope label, or the dropdown trigger + listbox when the response offers more than one scope (CO-02). */
function ScopeControl({
  scope,
  onChange,
}: {
  scope: StripResponse['scope'];
  onChange: (scope: StripScope) => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const options = scope.options.filter((option) => option.available);
  if (options.length <= 1) return <span className={styles.scopeLabel}>{scope.label}</span>;
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.scopeTrigger}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {scope.label}
        <Caret size={11} />
      </button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={triggerRef}
        placement="bottom-start"
        offset={4}
        width={280}
        variant="none"
        className={styles.scopeMenu}
        label="Bereich"
        initialFocus="first"
      >
        <div role="listbox" aria-label="Bereich" className={styles.scopeList}>
          {options.map((option) => (
            <button
              key={option.key}
              type="button"
              role="option"
              aria-selected={option.key === scope.key}
              className={clsx(styles.scopeOption, option.key === scope.key && styles.scopeOptionActive)}
              onClick={() => {
                onChange(option.key);
                setOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </Popover>
    </>
  );
}

/**
 * Controlling 01–03: the KPI strip above „Meine Tasks" — period, scope (dropdown only when the span
 * offers more than the own figures), five KPIs with deltas, „Details". Hidden while the server refuses
 * the figures (`controlling.figures` = none); the dashed „zu wenig Daten" row while not ready (H7).
 */
export function ControllingStrip({ className }: { className?: string | undefined }) {
  const [scope, setScope] = useState<StripScope>('me');
  const strip = useControllingStrip(scope, 'week');

  if (strip.isError) {
    if (isForbidden(strip.error)) return null;
    return (
      <div className={className}>
        <Banner tone="danger">{errorMessage(strip.error)}</Banner>
      </div>
    );
  }

  if (strip.isPending) {
    return (
      <div
        className={clsx(styles.strip, styles.loading, className)}
        aria-busy="true"
        aria-label="Wird geladen"
      >
        <div className={styles.scopeCell}>
          <Skeleton width={70} height={8} />
          <Skeleton width={90} />
        </div>
        <div className={styles.kpis}>
          {Array.from({ length: 5 }, (_, index) => (
            <div key={index} className={styles.kpi}>
              <Skeleton width={56} height={8} />
              <Skeleton width={40} height={22} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const data = strip.data;

  if (!data.ready) {
    return (
      <div className={clsx(styles.notReady, className)}>
        <Eyebrow>{data.period.label}</Eyebrow>
        <ScopeControl scope={data.scope} onChange={setScope} />
        <span className={styles.notReadyText}>{data.reason}</span>
      </div>
    );
  }

  return (
    <div className={clsx(styles.strip, className)}>
      <div className={styles.scopeCell}>
        <Eyebrow>{data.period.label}</Eyebrow>
        <ScopeControl scope={data.scope} onChange={setScope} />
      </div>
      <div className={styles.kpis}>
        {data.kpis.map((kpi) => (
          <div key={kpi.key} className={styles.kpi}>
            <Eyebrow className={styles.kpiLabel}>{kpi.label}</Eyebrow>
            <div className={styles.kpiValueRow}>
              <span className={styles.kpiValue}>{kpi.valueLabel}</span>
              {kpi.delta ? (
                <span
                  className={clsx(
                    styles.kpiDelta,
                    kpi.delta.tone === 'positive' && styles.positive,
                    kpi.delta.tone === 'negative' && styles.negative,
                  )}
                >
                  {kpi.delta.label}
                </span>
              ) : null}
              {kpi.extra ? <span className={styles.kpiExtra}>{kpi.extra}</span> : null}
            </div>
          </div>
        ))}
      </div>
      <div className={styles.detailsCell}>
        <Link to={data.detailsHref} className={styles.detailsLink}>
          Details
        </Link>
      </div>
    </div>
  );
}
